import type { CertificateHolder, CustomerDetail, CustomerSummary, FormDef, FormDraft, FormDraftView, FormRegistryView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { callAction } from "../api";
import { ModeBadge } from "../components/ModeBadge";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Select, Sheet, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { formatPhone, shortDate } from "../theme";

type DraftRow = FormDraft & { customerName: string; workflow: string; title: string };
const GROUPS: Array<FormDef["group"]> = ["certificate", "evidence", "service", "application"];
const REQUIREMENTS = ["additional_insured", "waiver_of_subrogation", "primary_noncontributory", "loss_payee", "mortgagee", "lienholder", "notice_30_days"] as const;

export function FormsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const [params, setParams] = useSearchParams();
  const registry = useQuery({ queryKey: ["form-registry"], queryFn: async () => (await callAction<"agency.form.registry", FormRegistryView>("agency.form.registry", {})).result, staleTime: 300_000 });
  const drafts = useQuery({ queryKey: ["form-drafts"], queryFn: async () => (await callAction<"agency.form.draft.list", DraftRow[]>("agency.form.draft.list", { limit: 100 })).result });
  const [starting, setStarting] = useState<FormDef | null>(null);
  const presetCustomer = params.get("customerId") ?? undefined;
  const presetForm = params.get("formId");

  useEffect(() => {
    if (presetForm && registry.data && !starting) {
      const def = registry.data.forms.find((f) => f.id === presetForm);
      if (def) setStarting(def);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetForm, registry.data]);

  if (registry.isLoading) return <LoadingState />;
  if (registry.isError || !registry.data) return <ErrorState error={registry.error} onRetry={() => registry.refetch()} />;
  const reg = registry.data;
  const demo = reg.providerMode !== "live";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{t("forms.title")}</h1>
          <p className="text-ink/60">{t("forms.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2"><ModeBadge mode={reg.providerMode} long /></div>
      </div>
      <p className={cn("rounded-2xl px-4 py-3 text-sm", demo ? "bg-accent/15 text-amber-900" : "bg-primary/10 text-primary-dark")}>{demo ? t("forms.providerDemo") : t("forms.providerLive")}</p>

      {GROUPS.map((g) => {
        const forms = reg.forms.filter((f) => f.group === g);
        if (!forms.length) return null;
        return (
          <section key={g}>
            <h2 className="text-lg font-bold mb-2">{t(`forms.group.${g}`)}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {forms.map((f) => (
                <Card key={f.id} className="p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold">{f.title[locale]}</p>
                      <p className="text-xs text-ink/55">{t("forms.workflow").replace("{n}", f.number)}</p>
                    </div>
                    <span className="h-9 w-9 shrink-0 rounded-xl bg-primary/12 text-primary-dark inline-flex items-center justify-center font-heading font-bold">{f.number}</span>
                  </div>
                  <p className="text-xs text-ink/60">{t("forms.intendedEdition")}: {f.intendedEdition}</p>
                  <p className="text-xs text-ink/60">{t("forms.lines")}: {f.lines.map((l) => l.replace(/_/g, " ")).join(", ")}</p>
                  {f.needsHolder ? <Badge tone="blue">{t("forms.needsHolder")}</Badge> : null}
                  <div className="mt-auto pt-1">
                    <Button size="sm" className="w-full" onClick={() => setStarting(f)}><FileSignature className="h-4 w-4" />{t("forms.start")}</Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        );
      })}

      <section>
        <h2 className="text-lg font-bold mb-2">{t("forms.history")}</h2>
        {drafts.isLoading ? <LoadingState /> : null}
        {drafts.data && drafts.data.length === 0 ? <EmptyState title={t("forms.noDrafts")} /> : null}
        {drafts.data && drafts.data.length > 0 ? (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink/55">
                <tr>
                  <th className="px-4 py-3">{t("forms.title")}</th>
                  <th className="px-4 py-3">{t("common.customer")}</th>
                  <th className="px-4 py-3">{t("common.status")}</th>
                  <th className="px-4 py-3 hidden sm:table-cell">{t("common.date")}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {drafts.data.map((d) => (
                  <tr key={d.id} className="border-t border-ink/5 hover:bg-primary/5">
                    <td className="px-4 py-2"><span className="font-semibold">ACORD {d.formNumber}</span> <span className="text-ink/60">· {d.workflow}</span> <span className="text-xs text-ink/50">{t("forms.version").replace("{n}", String(d.version))}</span></td>
                    <td className="px-4 py-2"><Link to={`/customers/${d.customerId}`} className="hover:underline">{d.customerName}</Link></td>
                    <td className="px-4 py-2"><Badge tone={d.status === "generated" ? "green" : "amber"}>{t(`forms.status.${d.status}`)}</Badge> {d.missing.length ? <span className="text-xs text-amber-800">{t("forms.unanswered").replace("{n}", String(d.missing.length))}</span> : null}</td>
                    <td className="px-4 py-2 hidden sm:table-cell">{shortDate(d.updatedAt, locale)}</td>
                    <td className="px-4 py-2 text-right"><Link to={`/forms/${d.id}`} className="inline-flex min-h-[36px] items-center font-semibold text-primary-dark hover:underline">{t("forms.open")} →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        ) : null}
      </section>

      <StartFormSheet def={starting} presetCustomerId={presetCustomer} onClose={() => { setStarting(null); if (presetForm) setParams(presetCustomer ? { customerId: presetCustomer } : {}); }} />
    </div>
  );
}

/** Pick customer → policy (for policy-sourced forms) → holder (for certificate forms) → start. */
export function StartFormSheet({ def, presetCustomerId, onClose }: { def: FormDef | null; presetCustomerId?: string; onClose: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [customerId, setCustomerId] = useState<string>(presetCustomerId ?? "");
  const [policyId, setPolicyId] = useState("");
  const [holderId, setHolderId] = useState("");
  const [addingHolder, setAddingHolder] = useState(false);

  useEffect(() => { setCustomerId(presetCustomerId ?? ""); setPolicyId(""); setHolderId(""); setQ(""); }, [def, presetCustomerId]);

  const search = useQuery({ queryKey: ["form-start-search", q], queryFn: async () => (await callAction<"agency.customer.search", CustomerSummary[]>("agency.customer.search", { q, limit: 8 })).result, enabled: !!def && !customerId && q.trim().length >= 2 });
  const detail = useQuery({ queryKey: ["customer", customerId], queryFn: async () => (await callAction<"agency.customer.get", CustomerDetail>("agency.customer.get", { customerId })).result, enabled: !!customerId });
  const holders = useQuery({ queryKey: ["holders", customerId], queryFn: async () => (await callAction<"agency.holder.list", CertificateHolder[]>("agency.holder.list", { customerId })).result, enabled: !!customerId && !!def?.needsHolder });

  const policies = useMemo(() => (detail.data?.policies ?? []).filter((p) => p.status !== "quoted"), [detail.data]);
  useEffect(() => { if (!policyId && policies[0]) setPolicyId(policies[0].id); }, [policies, policyId]);

  const start = useMutation({
    mutationFn: () => callAction<"agency.form.draft.start", FormDraftView>("agency.form.draft.start", { customerId, formId: def!.id, policyId: def!.sourceType === "policy" ? policyId : undefined, holderId: holderId || undefined }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["form-drafts"] });
      qc.invalidateQueries({ queryKey: ["customer", customerId] });
      toast({ title: t("forms.started"), body: `${r.result.draft.prefilled.length} ${t("forms.prefilledFrom")} · ${t("forms.unanswered").replace("{n}", String(r.result.draft.missing.length))}`, tone: "success" });
      onClose();
      navigate(`/forms/${r.result.draft.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  if (!def) return null;
  const canStart = !!customerId && (def.sourceType !== "policy" || !!policyId);
  return (
    <Sheet open={!!def} onClose={onClose} title={`${t("forms.startTitle")} · ACORD ${def.number}`}>
      <div className="space-y-4">
        <p className="text-sm text-ink/70">{def.workflow[locale]}</p>
        <div>
          <p className="mb-1 text-sm font-semibold text-ink/80">{t("forms.pickCustomer")}</p>
          {customerId ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-primary bg-primary/8 px-3 min-h-tap">
              <span className="font-semibold">{detail.data ? customerLabel(detail.data) : customerId}</span>
              {!presetCustomerId ? <button className="text-sm font-semibold text-primary-dark" onClick={() => { setCustomerId(""); setPolicyId(""); setHolderId(""); }}>{t("common.cancel")}</button> : null}
            </div>
          ) : (
            <>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("forms.searchCustomer")} autoFocus />
              <ul className="mt-2 space-y-1">
                {search.data?.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => setCustomerId(c.id)} className="flex w-full items-center justify-between rounded-xl border border-ink/10 px-3 min-h-tap text-left hover:border-primary">
                      <span className="font-semibold">{c.name}</span>
                      <span className="text-xs text-ink/55">{formatPhone(c.phone) ?? c.city ?? ""} · {c.policyCount} {t("common.policies").toLowerCase()}</span>
                    </button>
                  </li>
                ))}
                {search.data && search.data.length === 0 ? <li className="text-sm text-ink/55 px-1">{t("customers.none")}</li> : null}
              </ul>
            </>
          )}
        </div>

        {customerId && def.sourceType === "policy" ? (
          <div>
            {policies.length ? (
              <Select label={t("forms.pickPolicy")} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
                {policies.map((p) => <option key={p.id} value={p.id}>{p.carrier} · {p.line.replace(/_/g, " ")} · {p.policyNumber} · {shortDate(p.expirationDate, locale)}</option>)}
              </Select>
            ) : detail.isLoading ? <LoadingState /> : <p className="rounded-xl bg-accent/15 p-3 text-sm text-amber-900">{t("forms.noPolicies")} <Link className="font-semibold underline" to={`/customers/${customerId}`}>{t("common.open")}</Link></p>}
          </div>
        ) : null}

        {customerId && def.needsHolder ? (
          <div className="space-y-2">
            <Select label={t("forms.pickHolder")} value={holderId} onChange={(e) => setHolderId(e.target.value)}>
              <option value="">{t("forms.noHolder")}</option>
              {holders.data?.map((h) => <option key={h.id} value={h.id}>{h.name}{h.address ? ` · ${h.address.city}, ${h.address.state}` : ""}</option>)}
            </Select>
            {addingHolder ? (
              <HolderForm customerId={customerId} onDone={(h) => { setAddingHolder(false); qc.invalidateQueries({ queryKey: ["holders", customerId] }); setHolderId(h.id); }} onCancel={() => setAddingHolder(false)} />
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={() => setAddingHolder(true)}><Plus className="h-4 w-4" />{t("forms.addHolder")}</Button>
            )}
          </div>
        ) : null}

        <Button size="lg" className="w-full" disabled={!canStart} loading={start.isPending} onClick={() => start.mutate()}>{t("forms.startNow")}</Button>
      </div>
    </Sheet>
  );
}

function customerLabel(d: CustomerDetail): string {
  const c = d.customer;
  return c.kind === "business" && c.businessName ? c.businessName : `${c.firstName} ${c.lastName}`;
}

/** Inline certificate-holder editor, reused by the start sheet and the customer panel. */
export function HolderForm({ customerId, holder, onDone, onCancel }: { customerId: string; holder?: CertificateHolder; onDone: (h: CertificateHolder) => void; onCancel: () => void }) {
  const t = useT();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: holder?.name ?? "", line1: holder?.address?.line1 ?? "", city: holder?.address?.city ?? "", state: holder?.address?.state ?? "TX", zip: holder?.address?.zip ?? "",
    email: holder?.email ?? "", requirements: new Set<string>(holder?.requirements ?? []),
  });
  const save = useMutation({
    mutationFn: () => callAction<"agency.holder.upsert", CertificateHolder>("agency.holder.upsert", {
      id: holder?.id, customerId, name: form.name, email: form.email || undefined,
      address: form.line1 && form.city && form.zip ? { line1: form.line1, city: form.city, state: form.state.toUpperCase().slice(0, 2) || "TX", zip: form.zip } : undefined,
      requirements: [...form.requirements] as CertificateHolder["requirements"],
    }),
    onSuccess: (r) => { toast({ title: t("forms.holderSaved"), tone: "success" }); onDone(r.result); },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const toggle = (r: string) => setForm((f) => { const s = new Set(f.requirements); if (s.has(r)) s.delete(r); else s.add(r); return { ...f, requirements: s }; });
  return (
    <form className="rounded-2xl border border-ink/10 bg-surface p-3 space-y-2" onSubmit={(e) => { e.preventDefault(); if (form.name.trim()) save.mutate(); }}>
      <Input label={t("forms.holderName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      <Input label={t("forms.holderAddress")} value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Street" />
      <div className="grid grid-cols-[1fr_64px_90px] gap-2">
        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("common.city")} />
        <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="TX" maxLength={2} />
        <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="ZIP" />
      </div>
      <Input label={t("forms.holderEmail")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <div>
        <p className="mb-1 text-sm font-semibold text-ink/80">{t("forms.holderRequirements")}</p>
        <div className="flex flex-wrap gap-1">
          {REQUIREMENTS.map((r) => (
            <button type="button" key={r} onClick={() => toggle(r)} aria-pressed={form.requirements.has(r)} className={cn("rounded-full px-3 min-h-[36px] text-xs font-semibold border", form.requirements.has(r) ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/70 hover:bg-ink/5")}>{t(`forms.req.${r}`)}</button>
          ))}
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button type="submit" size="sm" loading={save.isPending}>{t("common.save")}</Button>
      </div>
    </form>
  );
}

